import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DeviceService } from '../../services/device.service';
import { DeviceDataStore } from '../../services/device-data.store';
import { AppStorageService, StorageKey } from '../../services/app-storage.service';

export const deviceReadyResolver: ResolveFn<boolean> = async (route) => {
  const api = inject(DeviceService);
  const store = inject(DeviceDataStore);
  const router = inject(Router);
  const storage = inject(AppStorageService);

  // Prefer route/query param, then fall back to session storage
  let deviceId =
    route.paramMap.get('deviceId') ||
    route.queryParamMap.get('deviceId') ||
    '';
  deviceId = (deviceId ?? '').trim();

  // Extract environment, currentStack, and hubName from query params for backend routing
  const environment = route.queryParamMap.get('environment') || '';
  const currentStack = route.queryParamMap.get('currentStack') || '';
  const hubName = route.queryParamMap.get('hubName') || '';

  if (!deviceId) {
    deviceId = (storage.getString(StorageKey.CURRENT_DEVICE_ID, 'session') ?? '').trim();
  }

  if (!deviceId) return true;

  // Store environment, currentStack, and hubName in session for backend API routing
  if (environment) {
    storage.setString(StorageKey.CURRENT_ENVIRONMENT, environment, 'session');
  }
  if (currentStack) {
    storage.setString(StorageKey.CURRENT_STACK, currentStack, 'session');
  }
  if (hubName) {
    storage.setString(StorageKey.CURRENT_HUB_NAME, hubName, 'session');
  }

  try {
    // Skip fetch if same device is already in store
    const existingId = (store.deviceId?.() ?? '').trim();
    const hasIdentity = !!store.identity?.();
    if (hasIdentity && existingId && existingId.toLowerCase() === deviceId.toLowerCase()) {
      await maybeNormalizeLegacyIdentityUrl(router, deviceId);
      return true;
    }

    // Set deviceId first so twin page can use it
    store.setDeviceId?.(deviceId);

    // Try to fetch identity - if it fails due to disconnected device, continue anyway
    try {
      const identity = await firstValueFrom(api.getIdentity(deviceId));
      store.setIdentity?.(identity);
    } catch (identityErr: any) {
      const errMsg = identityErr?.message || String(identityErr);
      // If device is disconnected, create minimal identity for twin page access
      if (errMsg.includes('not connected') || errMsg.includes('deviceDisconnected')) {
        store.setIdentity?.({
          deviceId: deviceId,
          status: 'Disabled',
          connectionState: 'Disconnected'
        });
      } else {
        // For other errors, still create minimal identity to allow twin access
        store.setIdentity?.({
          deviceId: deviceId,
          status: null,
          connectionState: 'Disconnected'
        });
      }
    }

    // Fetch twin to get connection state from twin properties
    try {
      const twin = await firstValueFrom(api.getTwin(deviceId));
      store.setTwin?.(twin);
      
      // Set connection state based on twin's connectionState property
      const twinConnectionState = twin?.connectionState?.toString().toLowerCase();
      if (twinConnectionState === 'connected') {
        store.setConnectionState?.('Connected');
      } else if (twinConnectionState === 'disconnected') {
        store.setConnectionState?.('Disconnected');
      } else {
        // Default to disconnected if not explicitly connected
        store.setConnectionState?.('Disconnected');
      }
    } catch (twinErr: any) {
      store.setConnectionState?.('Disconnected');
      // Don't throw - allow navigation even if twin fetch fails
    }

    // Persist per-tab for refresh
    storage.setString(StorageKey.CURRENT_DEVICE_ID, deviceId, 'session');

    await maybeNormalizeLegacyIdentityUrl(router, deviceId);
    return true;
  } catch (err: any) {
    // Still allow navigation - user can see error in the page
    return true;
  }
};

async function maybeNormalizeLegacyIdentityUrl(router: Router, deviceId: string): Promise<void> {
  const initialUrl = router.url || '';
  const isLegacyForm = initialUrl.startsWith('/devices/') && initialUrl.includes('/identity');
  if (!isLegacyForm) return;

  await router.navigate(['/device', 'device-identity'], {
    replaceUrl: true,
    queryParams: { deviceId },
    queryParamsHandling: 'merge',
  });
}
